import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    IconButton, 
    InputAdornment,
    CircularProgress,
    useTheme,
    alpha,
    InputBase
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MicIcon from '@mui/icons-material/Mic'; 
const PassiveSearchView = ({
    jobDescription, // Prop from container
    onJobDescriptionChange, // Prop from container
    onSubmit, // Prop from container (this is the container's submit handler)
    isProcessing,
    error,
    onHowSearchWorksClick
}) => {

    const theme = useTheme();
    console.log("VIEW RENDER: Props received by PassiveSearchView:", { jobDescription, onJobDescriptionChangeExists: !!onJobDescriptionChange, onSubmitExists: !!onSubmit, isProcessing, error });

    const location = useLocation();
    const [currentJobDescription, setCurrentJobDescription] = useState('');

    useEffect(() => {
        console.log("VIEW useEffect [location.state, jobDescription] - START");
        console.log("VIEW useEffect: Incoming prop jobDescription:", jobDescription);
        console.log("VIEW useEffect: location.state?.jobDescription:", location.state?.jobDescription);
        console.log("VIEW useEffect: Current internal currentJobDescription BEFORE update:", currentJobDescription);

        const initialDescFromLocation = location.state?.jobDescription;

        if (initialDescFromLocation) {
            console.log("VIEW useEffect: Using location.state.jobDescription:", initialDescFromLocation);
            setCurrentJobDescription(initialDescFromLocation);
            
        } else if (jobDescription !== undefined && jobDescription !== currentJobDescription) {
            // Sync with prop if location.state is not used AND prop is defined AND different from current internal state
            console.log("VIEW useEffect: Syncing currentJobDescription with prop jobDescription:", jobDescription);
            setCurrentJobDescription(jobDescription);
        }
        console.log("VIEW useEffect [location.state, jobDescription] - END. currentJobDescription AFTER update:", currentJobDescription); 
    }, [location.state, jobDescription]); 

    useEffect(() => {
        // This log helps see the final value of currentJobDescription after the above effect runs
        console.log("VIEW useEffect []: currentJobDescription is now:", currentJobDescription);
    }, [currentJobDescription]);


    const handleDescriptionChange = (event) => {
        const newValue = event.target.value;
        console.log("VIEW: handleDescriptionChange - New value from input:", newValue);
        setCurrentJobDescription(newValue);
        if (onJobDescriptionChange) {
            console.log("VIEW: handleDescriptionChange - Calling onJobDescriptionChange prop with:", newValue);
            onJobDescriptionChange(newValue);
        } else {
            console.warn("VIEW: handleDescriptionChange - onJobDescriptionChange prop is undefined.");
        }
    };

    const handleSubmit = () => {
        console.log("VIEW: local handleSubmit called. Current internal job description to be submitted:", currentJobDescription);
        if (onSubmit) {
            console.log("VIEW: local handleSubmit - Calling onSubmit prop with:", currentJobDescription);
            onSubmit(currentJobDescription);
        } else {
            console.warn("VIEW: local handleSubmit - onSubmit prop is undefined!");
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                maxWidth: '584px', 
                backgroundColor: 'transparent', 
                pt: 26
            }}
        >
            <Box
                component="img"
                src="ct_logo_black.png" 
                alt="Passive Candidates Logo"
                sx={{
                    width: { xs: 240, sm: 480 }, // Slightly smaller for a cleaner look
                    height: 'auto',
                    maxHeight: { xs: 75, sm: 120 },
                    mb: 3, // Consistent margin
                }}
            />

            {/* Pill-Shaped Search Bar */}
            <Paper
                elevation={0}
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    py: '0px', // Vertical padding for the pill
                    px: '8px', // Horizontal padding for the pill
                    display: 'flex',
                    alignItems: 'center', 
                    width: '100%',
                    mb: 2.5,
                    borderRadius: '28px',
                    backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[800],
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: `0 1px 2px 0 ${alpha(theme.palette.common.black, 0.05)}`,
                    '&:hover': {
                        boxShadow: `0 1px 3px 0 ${alpha(theme.palette.common.black, 0.1)}`,
                        backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[700],
                    },
                }}
            >
                <IconButton 
                    sx={{ 
                        p: '10px', // Standard IconButton padding
                        mt: '4px', // Nudge icon down slightly to align with first line of text
                    }} 
                    aria-label="search icon" 
                    onClick={handleSubmit} 
                    disabled={isProcessing}
                >
                    <SearchIcon sx={{ color: theme.palette.text.secondary, fontSize: '1.25rem' }} />
                </IconButton>
                <InputBase
                    sx={{
                        
                        ml: 0.5, // Reduced margin-left from icon
                        flex: 1,
                        fontSize: '0.95rem',
                        color: theme.palette.text.primary,
                        alignSelf: 'stretch', // Make InputBase take full height of flex container
                        display: 'flex', // To allow vertical centering of its input if needed (though textarea handles it)
                        '& .MuiInputBase-input': {
                            paddingTop: 4,
                            lineHeight: 1.5, 
                            minHeight: '24px', // Removed, let rows define height
                            width: '100%', // Ensure input takes full width of InputBase
                            resize: 'none', // Prevent manual resize of textarea
                            border: 'none', // Remove any default textarea border
                            outline: 'none', // Remove outline on focus
                            backgroundColor: 'transparent', // Ensure no conflicting background
                        },
                         '& .MuiInputBase-input::placeholder': { 
                            paddingTop: '3px', // Standard IconButton padding
                            color: theme.palette.text.disabled,
                            opacity: 1,
                        },
                    }}
                    placeholder="Describe your ideal candidate..."
                    inputProps={{ 'aria-label': 'enter job description skills or keywords' }}
                    value={currentJobDescription}
                    onChange={handleDescriptionChange}
                    disabled={isProcessing}
                    multiline
                    minRows={1} // Start as effectively single line
                    inputComponent="textarea"
                />
            </Paper>

            {error && (
                <Typography color="error" variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
                    {error}
                </Typography>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: '12px', mb: 6 }}>
                <Button
                    type="submit" 
                    variant="contained"
                    disabled={isProcessing || !currentJobDescription.trim()} // Also disable if no text
                    onClick={handleSubmit} // This now calls the local handleSubmit
                    sx={{
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#303134' : '#f8f9fa',
                        color: (theme) => theme.palette.mode === 'dark' ? '#e8eaed' : '#3c4043',
                        border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? '#303134' : '#f8f9fa'}`,
                        textTransform: 'none',
                        fontSize: '14px',
                        fontWeight: 400,
                        borderRadius: '8px',
                        padding: '0 16px',
                        height: '36px',
                        minWidth: '54px',
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 1px 1px rgba(0,0,0,0.1)' : '0 1px 1px rgba(0,0,0,0.1)',
                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#3c4043' : '#f1f3f4',
                            borderColor: (theme) => theme.palette.mode === 'dark' ? '#5f6368' : '#dadce0',
                            color: (theme) => theme.palette.mode === 'dark' ? '#e8eaed' : '#202124',
                        },
                        '&.Mui-disabled': { 
                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(48, 49, 52, 0.7)' : 'rgba(248, 249, 250, 0.7)',
                            color: (theme) => theme.palette.mode === 'dark' ? '#9aa0a6' : '#bdc1c6',
                            borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(48, 49, 52, 0.7)' : 'rgba(248, 249, 250, 0.7)',
                        }
                    }}
                >
                    {"Find Top Candidates"}
                </Button>
                <Button variant="text"
                        color="inherit"
                        size="small"
                        onClick={onHowSearchWorksClick}
                        sx={{
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#303134' : '#f8f9fa',
                        color: (theme) => theme.palette.mode === 'dark' ? '#e8eaed' : '#3c4043',
                        border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? '#303134' : '#f8f9fa'}`,
                        textTransform: 'none',
                        fontSize: '14px',
                        fontWeight: 400,
                        borderRadius: '8px',
                        padding: '0 16px',
                        height: '36px',
                        minWidth: '54px',
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 1px 1px rgba(0,0,0,0.1)' : '0 1px 1px rgba(0,0,0,0.1)',
                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#3c4043' : '#f1f3f4',
                            borderColor: (theme) => theme.palette.mode === 'dark' ? '#5f6368' : '#dadce0',
                            color: (theme) => theme.palette.mode === 'dark' ? '#e8eaed' : '#202124',
                        },
                        '&.Mui-disabled': { 
                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(48, 49, 52, 0.7)' : 'rgba(248, 249, 250, 0.7)',
                            color: (theme) => theme.palette.mode === 'dark' ? '#9aa0a6' : '#bdc1c6',
                            borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(48, 49, 52, 0.7)' : 'rgba(248, 249, 250, 0.7)',
                        }
                    }}
                    >
                        How search works
                </Button>
            </Box>
        </Paper>
    );
};

export default PassiveSearchView;
